import { PlacementWithLogical } from "@chakra-ui/react";

import { useLocalStorage } from "@/services/localstorage/useLocalStorage";
import {
  onboarding_complete_event,
  onboarding_disable_event,
  onboarding_enable_event,
  onboarding_next_event,
  onboarding_start_event,
} from "@/services/posthog/events";
import { enable_promptbook_onboarding_flag } from "@/services/posthog/feature_flags";
import { useAnalytics } from "@/services/posthog/hooks";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { HelpButton } from "./HelpButton";
import { OnboardingModal } from "./OnboardingModal";
import { OnboardingStep } from "./OnboardingStep";
import { sequences } from "./steps";

export const OnboardingOverlay = () => {
  const [_showOnboarding, setShowOnboarding] = useLocalStorage<boolean>("show:onboarding", true);
  const [skipped, setSkipped] = useLocalStorage<string[]>("skip:onboarding", []);
  const [selectedSequence, setSelectedSequence] = useLocalStorage<string | null>("selected:onboarding", null);
  const [completedSequences, setCompletedSequences] = useLocalStorage<string[]>("completed:onboarding", []);
  const analytics = useAnalytics();
  const isOnboardingEnabled = useFeatureFlagEnabled(enable_promptbook_onboarding_flag);

  const sequence = sequences[selectedSequence as keyof typeof sequences]?.steps;
  const remainingSteps = sequence?.filter((step) => !skipped.includes(step["id"])) || [];
  const stepsTotal = sequence?.length || 0;
  const currentStep = remainingSteps[0];
  const currentStepIndex = sequence?.findIndex((e) => e.id === currentStep?.id);

  const showOnboarding = _showOnboarding && isOnboardingEnabled;

  const onToggleOnboarding = (shouldShow = !showOnboarding) => {
    analytics.capture(shouldShow ? onboarding_enable_event : onboarding_disable_event);

    setShowOnboarding(shouldShow);

    if (shouldShow) {
      setSkipped([]);
    } else {
      setSelectedSequence(null);
    }
  };

  const onSelectSequence = (sequence: string) => {
    // reset steps in that sequence
    setSkipped([]);
    setSelectedSequence(sequence);
    analytics.capture(onboarding_start_event, { sequence });
  };

  const onNextStep = () => {
    analytics.capture(onboarding_next_event, { step: currentStep["id"] });
    setSkipped([...skipped, currentStep["id"]]);

    // if the current step is the last step in the sequence
    if (currentStepIndex === stepsTotal - 1) {
      analytics.capture(onboarding_complete_event, { step: currentStep["id"] });
      setCompletedSequences(Array.from(new Set([...(completedSequences || []), selectedSequence as string])));
    }
  };

  return (
    <>
      {showOnboarding === true && (selectedSequence === null || !currentStep) && (
        <OnboardingModal
          sequences={sequences}
          completedSequences={completedSequences}
          onSelectSequence={onSelectSequence}
          onToggleOnboarding={onToggleOnboarding}
        />
      )}
      {showOnboarding && currentStep && (
        <OnboardingStep
          actionLabel={currentStep["actionLabel"]}
          currentStep={currentStepIndex + 1}
          stepsTotal={stepsTotal}
          image={currentStep["image"]}
          id={currentStep["id"]}
          placement={currentStep["placement"] as PlacementWithLogical}
          target={currentStep["target"]}
          title={currentStep["title"]}
          description={currentStep["description"]}
          onGotIt={onNextStep}
          onToggleOnboarding={onToggleOnboarding}
        />
      )}
      <HelpButton showOnboarding={showOnboarding || false} onToggleOnboarding={onToggleOnboarding} />
    </>
  );
};
